(() => {
  if (window.VendorTimerDiscountInitialized) return;
  window.VendorTimerDiscountInitialized = true;

  const STORAGE_KEY = 'vlTimerDiscountState';
  const ROOT = window.Shopify?.routes?.root || '/';

  let isUpdatingDiscount = false;
  let lastAppliedSignature = null;
  let syncTimer = null;
  let originalFetch = null;
  let cartDrawerRenderPatched = false;

  const htmlDecoder = document.createElement('textarea');
  const decodeHtmlEntities = (val) => {
    htmlDecoder.innerHTML = String(val || '');
    return htmlDecoder.value;
  };
  const normalize = (val) => decodeHtmlEntities(val).trim().toLowerCase();

  const getSource = () => document.querySelector('[data-vl-timer-discount-rules]');

  const parseRules = () => {
    const source = getSource();
    if (!source) return [];

    return source.textContent
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [vendor, code, percent] = line.split('|').map((part) => part.trim());
        return { vendor, code, percent: Number(percent) };
      })
      .filter((rule) => rule.vendor && rule.code && rule.percent > 0);
  };

  const getDuration = () => Number(getSource()?.dataset.vlTimerDuration) || 300;

  const getState = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || null;
    } catch (e) {
      return null;
    }
  };

  const setState = (state) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  };

  const scheduleSync = (delay = 250, forceUpdate = true) => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => runSync(forceUpdate), delay);
  };

  const refreshDrawer = () => {
    const drawer = document.querySelector('m-cart-drawer');

    if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
      drawer.onCartDrawerUpdate(true);
    }
  };

  // Find vendors currently in the cart items
  const getEligibleRulesFromDom = (rules) => {
    const items = document.querySelectorAll('[data-cart-item][data-vendor]');
    if (!items.length) return [];

    const vendorsInCart = new Set(Array.from(items).map((item) => normalize(item.dataset.vendor)));
    return getEligibleRulesFromVendorSet(rules, vendorsInCart);
  };

  const getEligibleRulesFromHtml = (html, rules) => {
    if (!html) return [];

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = doc.querySelectorAll('[data-cart-item][data-vendor]');
    if (!items.length) return [];

    const vendorsInCart = new Set(Array.from(items).map((item) => normalize(item.dataset.vendor)));
    return getEligibleRulesFromVendorSet(rules, vendorsInCart);
  };

  const getEligibleRulesFromVendorSet = (rules, vendorsInCart) => {
    const seen = new Set();

    return rules.filter((rule) => {
      const normVendor = normalize(rule.vendor);

      if (vendorsInCart.has(normVendor) && !seen.has(rule.code)) {
        seen.add(rule.code);
        return true;
      }

      return false;
    });
  };

  const applyDiscountToShopify = async (codes, signature, options = {}) => {
    if (isUpdatingDiscount) return;

    const shouldRefreshDrawer = options.refreshDrawer !== false;

    isUpdatingDiscount = true;

    try {
      await fetch(`${ROOT}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          discount: Array.isArray(codes) ? codes.join(',') : codes || ''
        })
      });

      lastAppliedSignature = signature;

      if (shouldRefreshDrawer) {
        // Shopify sometimes needs a short moment to recalculate the discounted cart.
        setTimeout(refreshDrawer, 300);
      }
    } catch (e) {
      console.error('VL Discount Error:', e);
      lastAppliedSignature = null;
    } finally {
      setTimeout(() => {
        isUpdatingDiscount = false;
      }, 800);
    }
  };

  const fetchCartDrawerSection = async () => {
    if (!window.MinimogSettings?.routes?.cart) return null;

    const response = await fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer`);
    return response.ok ? response.text() : null;
  };

  const preRenderDiscountedCartDrawer = async (cartResponse) => {
    const rules = parseRules();
    const drawerHtml = cartResponse?.sections?.['cart-drawer'];

    if (!rules.length || !drawerHtml) return cartResponse;

    const eligible = getEligibleRulesFromHtml(drawerHtml, rules);
    const signature = eligible.map((rule) => rule.code).sort().join(',');

    if (!signature || signature === lastAppliedSignature || isUpdatingDiscount) {
      return cartResponse;
    }

    await applyDiscountToShopify(
      eligible.map((rule) => rule.code),
      signature,
      { refreshDrawer: false }
    );

    const refreshedDrawerHtml = await fetchCartDrawerSection();

    if (refreshedDrawerHtml) {
      cartResponse.sections['cart-drawer'] = refreshedDrawerHtml;
    }

    return cartResponse;
  };

  const runSync = (forceUpdate = true) => {
    const rules = parseRules();
    if (!rules.length) return;

    const eligible = getEligibleRulesFromDom(rules);
    const signature = eligible.map((rule) => rule.code).sort().join(',');
    const now = Date.now();
    const currentState = getState();

    let nextState =
      currentState &&
      currentState.signature === signature &&
      currentState.endsAt > now &&
      !currentState.expired
        ? currentState
        : {
            signature,
            codes: eligible.map((rule) => rule.code),
            endsAt: now + getDuration() * 1000,
            expired: false
          };

    if (eligible.length > 0) {
      setState(nextState);
    } else {
      nextState = {
        signature: '',
        codes: [],
        endsAt: now,
        expired: true
      };
    }

    // Visual: update line chips
    const activeCodes = new Set(nextState.codes);

    document.querySelectorAll('[data-cart-item]').forEach((item) => {
      const chip = item.querySelector('[data-vl-line-discount-chip]');
      if (!chip) return;

      const rule = rules.find(
        (candidate) =>
          normalize(candidate.vendor) === normalize(item.dataset.vendor) &&
          activeCodes.has(candidate.code)
      );

      chip.hidden = !rule;

      if (rule) {
        chip.textContent = `${rule.percent}% off`;
      }
    });

    // Visual: update timer banner
    const banner = document.querySelector('[data-vl-timer-discount-cart]');

    if (banner) {
      const sec = Math.max(0, Math.ceil((nextState.endsAt - Date.now()) / 1000));
      banner.hidden = sec <= 0 || eligible.length === 0;

      const timeEl = banner.querySelector('[data-vl-timer-discount-time]');

      if (timeEl && !banner.hidden) {
        const minutes = Math.floor(sec / 60);
        const seconds = sec % 60;

        timeEl.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }
    }

    // Actual Shopify discount session update
    if (forceUpdate && signature !== lastAppliedSignature && !isUpdatingDiscount) {
      applyDiscountToShopify(nextState.codes, signature);
    }
  };

  const observeCartDrawerChanges = () => {
    const drawerItems = document.querySelector('[data-minimog-cart-items]');
    if (!drawerItems) return;

    const observer = new MutationObserver(() => {
      if (!isUpdatingDiscount) {
        scheduleSync(150, true);
      } else {
        scheduleSync(900, false);
      }
    });

    observer.observe(drawerItems, {
      childList: true,
      subtree: true
    });
  };

  const subscribeToThemeEvents = () => {
    document.addEventListener('cart:updated', () => {
      scheduleSync(250, true);
    });

    if (window.MinimogEvents?.subscribe && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, () => {
        scheduleSync(350, true);
      });
    }
  };

  const patchFetch = () => {
    if (originalFetch) return;

    originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const result = await originalFetch(...args);
      const url = String(args[0]?.url || args[0] || '');

      if (/\/cart\/(add|change|clear)\.js/.test(url) && !isUpdatingDiscount) {
        scheduleSync(650, true);
      }

      return result;
    };
  };

  const patchCartDrawerRender = () => {
    if (cartDrawerRenderPatched) return;

    const CartDrawer = customElements.get('m-cart-drawer');
    if (!CartDrawer?.prototype?.renderContents) return;

    const originalRenderContents = CartDrawer.prototype.renderContents;

    CartDrawer.prototype.renderContents = async function (cartResponse) {
      let responseToRender = cartResponse;

      try {
        responseToRender = await preRenderDiscountedCartDrawer(cartResponse);
      } catch (e) {
        console.error('VL Discount Pre-render Error:', e);
      }

      return originalRenderContents.call(this, responseToRender);
    };

    cartDrawerRenderPatched = true;
  };

  const init = () => {
    runSync(true);
    subscribeToThemeEvents();
    observeCartDrawerChanges();
    patchFetch();
    patchCartDrawerRender();
  };

  // Timer for the countdown clock only
  setInterval(() => runSync(false), 1000);

  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init);
  }
})();
